import { useEffect, useRef, useState } from "react";
import { Menu, X, Tag, Calendar as CalendarIcon, ChevronDown, Check } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useLang, LANG_META } from "../../lib/LangContext";
import { buildWhatsAppLink } from "../../lib/translations";
import { navigateAndScroll } from "../../lib/scroll";
import { useLocalePath } from "./LangRouting";
import { splitPath } from "../../lib/i18nRoutes";

// Main navigation: section links + Academy page link.
// Note: "Before / After" section still exists on the page but is intentionally
// removed from the top nav to keep the header from overflowing in DE / UA where
// labels are longer. Secondary items (Service Area, Promotions) are hidden
// below 2xl to keep the header readable on 1280-1535 px screens.
const NAV_LINKS = [
  { id: "home", key: "home", priority: 1 },
  { id: "services", key: "services", priority: 1 },
  { id: "faq", key: "faq", priority: 1 },
  { id: "contact", key: "contact", priority: 1 },
];

const LangSwitcher = ({ testid }) => {
  const { lang, setLang, currentMeta, meta } = useLang();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown, { passive: true });
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={ref} data-testid={testid} className="relative">
      <button
        type="button"
        data-testid={`${testid}-trigger`}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="inline-flex items-center gap-1.5 border border-white/15 hover:border-gold/60 px-2.5 py-1.5 text-[11px] font-medium tracking-[0.14em] uppercase text-zinc-200 hover:text-gold transition-colors leading-none"
      >
        <span className="text-sm leading-none" aria-hidden="true">{currentMeta.flag}</span>
        <span>{currentMeta.code.toUpperCase()}</span>
        <ChevronDown
          size={10}
          className={`transition-transform ${open ? "rotate-180" : ""}`}
          aria-hidden="true"
        />
      </button>
      {open && (
        <ul
          role="listbox"
          data-testid={`${testid}-menu`}
          className="absolute right-0 top-[calc(100%+6px)] z-[130] min-w-[200px] bg-[#0a0a0c] border border-gold/40 shadow-[0_20px_50px_-15px_rgba(0,0,0,0.9)] py-1 animate-in fade-in zoom-in-95 duration-150"
        >
          {meta.map((m) => (
            <li key={m.code} role="none">
              <button
                type="button"
                role="option"
                aria-selected={m.code === lang}
                data-testid={`${testid}-option-${m.code}`}
                onClick={() => { setLang(m.code); setOpen(false); }}
                className={`w-full flex items-center gap-3 px-3 py-2 text-[12px] transition-colors ${
                  m.code === lang
                    ? "text-gold bg-gold/10"
                    : "text-zinc-200 hover:text-gold hover:bg-white/5"
                }`}
              >
                <span className="text-base leading-none" aria-hidden="true">{m.flag}</span>
                <span className="font-medium tracking-[0.14em] uppercase text-[11px] w-7">{m.code.toUpperCase()}</span>
                <span className="text-zinc-400 text-[11px]" aria-hidden="true">—</span>
                <span className="flex-1 text-left">{m.native}</span>
                {m.code === lang && <Check size={12} className="text-gold" aria-hidden="true" />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const Navigation = () => {
  const { lang, t } = useLang();
  const lp = useLocalePath();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Hide the main site navigation on the PUREGARAGE product landing.
  // That page ships its own premium navigation component.
  const isPureGarage = location.pathname === "/app" || location.pathname.startsWith("/app/");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const closeMobile = () => setOpen(false);

  const goSection = (id) => {
    closeMobile();
    navigateAndScroll(navigate, id);
  };

  const goAcademy = () => {
    closeMobile();
    navigate(lp("/academy"));
  };

  const goContact = (flow) => {
    closeMobile();
    navigateAndScroll(navigate, "contact", { flow });
  };

  if (isPureGarage) return null;

  return (
    <header
      data-testid="nav-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled || splitPath(location.pathname).rest !== "/" ? "glass-strong py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-[1600px] mx-auto px-3 sm:px-5 lg:px-8 flex items-center justify-between gap-2 md:gap-3">
        <button
          data-testid="nav-logo"
          onClick={() => goSection("home")}
          className="flex flex-col items-start leading-none group shrink-0"
        >
          <span className="font-heading text-white text-lg md:text-xl xl:text-2xl tracking-tighter">
            PURE<span className="text-gold">GLOSS</span>
          </span>
          <span className="hidden sm:block text-[9px] md:text-[10px] tracking-[0.32em] uppercase text-zinc-500 mt-1 group-hover:text-zinc-300 transition-colors">
            Detailing & Sofa Cleaning
          </span>
        </button>

        <nav className="hidden xl:flex items-center gap-3 2xl:gap-5 min-w-0">
          {NAV_LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => goSection(l.id)}
              className={`${l.priority === 2 ? "hidden 2xl:inline-flex" : "inline-flex"} text-[11px] 2xl:text-[12px] tracking-[0.12em] 2xl:tracking-[0.16em] uppercase text-zinc-300 hover:text-gold transition-colors whitespace-nowrap`}
            >
              {t.nav[l.key]}
            </button>
          ))}
          <Link
            data-testid="nav-link-works"
            to={lp("/arbeiten")}
            className="text-[11px] 2xl:text-[12px] tracking-[0.12em] 2xl:tracking-[0.16em] uppercase text-zinc-300 hover:text-gold transition-colors whitespace-nowrap"
          >
            {t.nav.works}
          </Link>
          <Link
            data-testid="nav-link-puregarage"
            to="/app"
            className="text-[11px] 2xl:text-[12px] tracking-[0.12em] 2xl:tracking-[0.16em] uppercase text-zinc-300 hover:text-gold transition-colors whitespace-nowrap"
          >
            {t.puregarage.nav}
          </Link>
          <Link
            data-testid="nav-link-academy"
            to={lp("/academy")}
            className="text-[11px] 2xl:text-[12px] tracking-[0.12em] 2xl:tracking-[0.16em] uppercase text-gold/90 hover:text-gold transition-colors border border-gold/40 px-2 2xl:px-2.5 py-1 whitespace-nowrap"
          >
            {t.nav.academy}
          </Link>
        </nav>

        <div className="flex items-center gap-1.5 md:gap-2 shrink-0">
          <LangSwitcher testid="lang-switcher" />

          {/* Primary CTAs — visible from md+ */}
          <button
            data-testid="nav-cta-price"
            onClick={() => goContact("price")}
            className="hidden md:inline-flex items-center gap-1.5 border border-gold text-gold px-2.5 lg:px-3 py-2 text-[11px] tracking-[0.1em] lg:tracking-[0.14em] uppercase font-medium hover:bg-gold/10 transition-colors whitespace-nowrap"
            title={t.cta.getPrice}
          >
            <Tag size={12} /> {t.cta.getPriceShort}
          </button>
          <button
            data-testid="nav-cta-book"
            onClick={() => goContact("book")}
            className="hidden md:inline-flex items-center gap-1.5 bg-gold text-black px-2.5 lg:px-3 py-2 text-[11px] tracking-[0.1em] lg:tracking-[0.14em] uppercase font-semibold shine-hover hover:bg-[#f3e5ab] transition-colors whitespace-nowrap"
            title={t.cta.bookAppointment}
          >
            <CalendarIcon size={12} /> {t.cta.bookAppointmentShort}
          </button>

          {/* WhatsApp secondary — only shown when there's enough room (>=2xl) */}
          <a
            data-testid="nav-whatsapp-cta"
            href={buildWhatsAppLink("Hi PUREGLOSS! ")}
            target="_blank"
            rel="noreferrer"
            aria-label="WhatsApp"
            className="hidden 2xl:inline-flex items-center justify-center w-9 h-9 border border-white/15 text-zinc-300 hover:text-gold hover:border-gold transition-colors"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
              <path d="M.057 24l1.687-6.163a11.867 11.867 0 0 1-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 0 1 8.413 3.488 11.824 11.824 0 0 1 3.48 8.414c-.003 6.555-5.338 11.892-11.893 11.892a11.9 11.9 0 0 1-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 0 0 1.51 5.26l.241.383-1 3.652 3.738-.994zM18.05 14.55c-.075-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.471-.148-.67.15-.197.297-.768.967-.941 1.166-.173.198-.347.223-.644.074-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.297-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413z"/>
            </svg>
          </a>

          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className="xl:hidden text-white p-2"
            aria-label="menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="nav-mobile-menu"
          className="xl:hidden glass-strong border-t border-white/5 px-6 py-6"
        >
          <nav className="flex flex-col gap-3">
            {NAV_LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-mobile-link-${l.id}`}
                onClick={() => goSection(l.id)}
                className="text-left text-sm tracking-[0.2em] uppercase text-zinc-200 hover:text-gold transition-colors py-1.5"
              >
                {t.nav[l.key]}
              </button>
            ))}
            <Link
              to={lp("/arbeiten")}
              data-testid="nav-mobile-link-works"
              onClick={closeMobile}
              className="text-left text-sm tracking-[0.2em] uppercase text-zinc-200 hover:text-gold transition-colors py-1.5"
            >
              {t.nav.works}
            </Link>
            <Link
              to="/app"
              data-testid="nav-mobile-link-puregarage"
              onClick={closeMobile}
              className="text-left text-sm tracking-[0.2em] uppercase text-zinc-200 hover:text-gold transition-colors py-1.5"
            >
              {t.puregarage.nav}
            </Link>
            <button
              data-testid="nav-mobile-link-academy"
              onClick={goAcademy}
              className="text-left text-sm tracking-[0.2em] uppercase text-gold hover:text-[#f3e5ab] transition-colors py-1.5"
            >
              {t.nav.academy}
            </button>
            <div className="mt-3 pt-3 border-t border-white/10 flex flex-col gap-2.5">
              <button
                data-testid="nav-mobile-cta-price"
                onClick={() => goContact("price")}
                className="inline-flex items-center justify-center gap-2 border border-gold text-gold px-4 py-2.5 text-[11px] tracking-[0.2em] uppercase"
              >
                <Tag size={12} /> {t.cta.getPrice}
              </button>
              <button
                data-testid="nav-mobile-cta-book"
                onClick={() => goContact("book")}
                className="inline-flex items-center justify-center gap-2 bg-gold text-black px-4 py-2.5 text-[11px] tracking-[0.2em] uppercase font-semibold"
              >
                <CalendarIcon size={12} /> {t.cta.bookAppointment}
              </button>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};
